import React, { useState, useEffect, useCallback } from 'react';
import { ResizableTable } from './ResizableTable';

const waitlistColumns = {
    email: 220,
    createdAt: 180,
    status: 120,
};

type WaitlistSectionProps = {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
};

export const WaitlistSection: React.FC<WaitlistSectionProps> = ({ API_BASE, authHeaders }) => {
    const [waitlist, setWaitlist] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchWaitlist = useCallback(() => {
        setLoading(true);
        fetch(`${API_BASE}/waitlist`, {
            method: 'GET',
            headers: authHeaders(),
        })
            .then(async (res) => {
                const text = await res.text();
                try {
                    if (!res.ok) throw new Error(text);
                    const data = JSON.parse(text);
                    // Only keep email, createdAt, and status
                    const filtered = (data.waitlist || []).map((item: any) => ({
                        email: item.email,
                        createdAt: item.createdAt,
                        status: item.status,
                    }));
                    setWaitlist(filtered);
                } catch (err: any) {
                    setError(
                        `Error: ${err.message || 'Failed to fetch waitlist'}\nRaw response:\n${text}`
                    );
                }
            })
            .catch((err) => setError(`Fetch error: ${err.message}`))
            .finally(() => setLoading(false));
    }, [API_BASE, authHeaders]);

    useEffect(() => {
        fetchWaitlist();
    }, [fetchWaitlist]);

    // Handler for deleting selected waitlist entries
    const handleDelete = async (selected: any[]) => {
        if (!selected || selected.length === 0) return;
        setLoading(true);
        setError(null);
        try {
            const emails = selected.map((item) => item.email);
            const res = await fetch(`${API_BASE}/waitlist`, {
                method: 'DELETE',
                headers: {
                    ...authHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(emails),
            });
            const text = await res.text();
            if (!res.ok) throw new Error(text);
            fetchWaitlist();
        } catch (err: any) {
            setError(
                `Delete error: ${err.message || 'Failed to delete waitlist entries'}`
            );
        } finally {
            setLoading(false);
        }
    };

    // Handler for sending invites to selected waitlist entries
    const handleSendInvites = async (selected: any[]) => {
        if (!selected || selected.length === 0) return;
        setLoading(true);
        setError(null);
        try {
            const emails = selected.map((item) => item.email);
            const res = await fetch(`${API_BASE}/admin/invite`, {
                method: 'POST',
                headers: {
                    ...authHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ emails }),
            });
            const text = await res.text();
            if (!res.ok) throw new Error(text);
            // Optionally, you could show a success message or refresh the waitlist
            fetchWaitlist();
        } catch (err: any) {
            setError(
                `Invite error: ${err.message || 'Failed to send invites'}`
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="table-section">
            <h2>Waitlist</h2>
            {loading && <div>Loading...</div>}
            {error && (
                <div style={{ color: 'red', whiteSpace: 'pre-wrap' }}>
                    {error}
                </div>
            )}
            <ResizableTable
                columns={waitlistColumns}
                data={waitlist}
                onMassActionSubmit={() => {}}
                onRefresh={fetchWaitlist}
                refreshButton={true}
                sortable={true}
                sortableColumns={['email', 'createdAt', 'status']}
                deleteButton={true}
                onDelete={handleDelete}
                submitButton={true}
                onSubmit={handleSendInvites}
                submitLabel="Send Invites"
            />
        </section>
    );
};
